"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkVoucherCheckResponse = exports.LinkVoucherStatus = exports.LinkVoucherCheckRequest = exports.LinkVoucherCode = exports.LINK_LOYALTY_VOUCHER_CHECK_PATH = exports.LinkOsrSetOnlineResponse = exports.LinkOsrSetOnlineRequest = exports.OSR_CHANNEL_CODE = exports.LINK_OSR_SET_ONLINE_ATTEMPTS = exports.LINK_OSR_SET_ONLINE_ATTEMPT_TIMEOUT_MS = exports.LINK_LOYALTY_OSR_SET_ONLINE_PATH = exports.LinkOpenReceiptProduct = exports.LinkOpenReceiptBasketId = exports.LinkLoyaltyReceiptResponse = exports.LinkLoyaltyReceiptState = exports.LinkLoyaltyReceiptRequest = exports.LINK_LOYALTY_RECEIPT_PATH = exports.LinkLoyaltyError = exports.LinkLoyaltyErrorCode = exports.LINK_LOYALTY_ERROR_STATUS = exports.LinkReceiptPreference = exports.LinkLoyaltyLookupResponse = exports.LinkLoyaltyMemberRef = exports.LINK_LOYALTY_MAX_BALANCES = exports.LinkLoyaltyBalance = exports.LinkLoyaltyBalanceKind = exports.LinkLoyaltyLookupRequest = exports.LoyaltyIdentifierKind = exports.LINK_LOYALTY_TILL_TIMEOUT_MS = exports.LINK_LOYALTY_LOOKUP_PATH = void 0;
exports.linkLoyaltyReceiptPreferencePath = linkLoyaltyReceiptPreferencePath;
const zod_1 = require("zod");
const loyalty_js_1 = require("./loyalty.js");
const sync_js_1 = require("./sync.js");
const link_crm_purchase_js_1 = require("./link-crm-purchase.js");
/**
 * Till → re:form.lite.link loyalty surface (RL-569, ADR-0014 amendment "CRM
 * connector on the Link").
 *
 * A store whose `connectors.loyaltySource` is `link` resolves loyalty
 * customers through its Link, which asks the store's CRM loyalty source
 * (integration profile `ip-3`) over the store network. The routes live on the
 * Link's LAN listener, behind the same store-pass guard as `/sync/*`, and
 * additionally need the pass's `loyalty` scope (`store-pass.ts`,
 * `STORE_PASS_SCOPES`).
 *
 *   POST /loyalty/lookup                                  body LinkLoyaltyLookupRequest
 *   GET  /loyalty/members/{memberRef}/receipt-preference
 *   POST /loyalty/receipts                                body LinkLoyaltyReceiptRequest (RL-587)
 *   POST /loyalty/online-orders/set-online               VL-384, see the end of this file
 *   POST /loyalty/vouchers/check                          VL-26 successor (dormant), at the end
 *
 * ------------------------------------------------------------------
 * RULES BOTH ENDS KEEP
 * ------------------------------------------------------------------
 * - **The identifier travels in a body, never in a URL.** A card number or a
 *   phone is personal data, and a query string ends up in access logs, proxy
 *   logs and client exception messages.
 * - **The kind is explicit.** The cashier chose card or phone; a Link never
 *   guesses one from the digits and never falls back from one to the other.
 * - **Live, never cached.** Balances are the CRM's; neither the Link nor the
 *   till keeps one past the basket.
 * - **Only the CRM's documented "not found" is a miss (404).** Every other
 *   failure is an outage (503 / 502) and the sale continues without a card: a
 *   false outage is retried, a false miss tells a member their card does not
 *   exist.
 * - **No points.** `pointsBalance` is always 0. The CRM's balances are money,
 *   carried in `balances` for display only; redeeming them needs the CRM's
 *   reserve / commit operations, which this surface does not have.
 */
/** RL-569: `POST` — resolve a card or phone. */
exports.LINK_LOYALTY_LOOKUP_PATH = '/loyalty/lookup';
/** RL-569: `GET` — the paper-receipt preference of a member a lookup returned. */
function linkLoyaltyReceiptPreferencePath(memberRef) {
    return `/loyalty/members/${encodeURIComponent(memberRef)}/receipt-preference`;
}
/**
 * How long a till waits for a Link loyalty answer. Above the longest CRM
 * timeout a source may be given (`LINK_IP3_TIMEOUT_MAX_MS`, 20 s), so the Link
 * — which answers "unavailable" once the CRM times out — is never cut off by
 * the till first.
 */
exports.LINK_LOYALTY_TILL_TIMEOUT_MS = 25_000;
/** What the cashier entered: a loyalty card number, or a phone number. */
exports.LoyaltyIdentifierKind = zod_1.z.enum(['card', 'phone']);
/**
 * `POST /loyalty/lookup`. The identifier is checked against its kind's shape
 * (`LoyaltyMemberNumber` / `LoyaltyPhone`); a Link answers a body that fails
 * it with 400 `bad_request`, without calling the CRM.
 */
exports.LinkLoyaltyLookupRequest = zod_1.z
    .object({
    kind: exports.LoyaltyIdentifierKind,
    identifier: zod_1.z.string().min(1).max(64),
})
    .superRefine((request, ctx) => {
    const shape = request.kind === 'card' ? loyalty_js_1.LoyaltyMemberNumber : loyalty_js_1.LoyaltyPhone;
    if (!shape.safeParse(request.identifier).success) {
        // The value is never echoed: it is personal data.
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['identifier'],
            message: `not a ${request.kind === 'card' ? 'card number' : 'phone number'}`,
        });
    }
});
/**
 * - `bonus` — the member's active bonus balance;
 * - `cash` — a cash account: a different tender, never added to `bonus`.
 *
 * A reader skips a balance of a kind it does not know, so the list can grow.
 */
exports.LinkLoyaltyBalanceKind = zod_1.z.enum(['bonus', 'cash']);
/** One balance in integer minor units of `currency`. Display only. */
exports.LinkLoyaltyBalance = zod_1.z.object({
    kind: exports.LinkLoyaltyBalanceKind,
    amountMinor: zod_1.z.number().int(),
    currency: zod_1.z.string().regex(/^[A-Z]{3}$/, 'ISO 4217 code'),
});
/** Most balances one member carries on the wire. */
exports.LINK_LOYALTY_MAX_BALANCES = 8;
/**
 * An opaque, random reference (at least 128 bits, base64url) naming one
 * lookup in the Link's memory. Not derived from the identifier or the member.
 * Short-lived and bound to the till that looked the member up; unknown after
 * the Link restarts.
 */
exports.LinkLoyaltyMemberRef = zod_1.z.string().regex(/^[A-Za-z0-9_-]{22,64}$/);
/**
 * `200` of `POST /loyalty/lookup`: the terminal-facing member shape the till
 * already reads from Back Office (`LoyaltyMemberLookup`), plus optional
 * fields a Back Office lookup never carries.
 *
 * - `memberNumber` — the card number the cashier presented when the lookup
 *   was by card (the Link keeps it, never swaps in another card of the same
 *   account), else the member's card the CRM returned;
 * - `status` — `suspended` for a blocked card; the till refuses to attach it;
 * - `pointsBalance` — always 0 (see the header);
 * - `cardAccountNumber` — the CRM's account number for the member's card, for
 *   a later receipt feed; not shown;
 * - `memberRef` — for `GET …/receipt-preference`. Absent when the Link cannot
 *   answer one; the till then prints.
 */
exports.LinkLoyaltyLookupResponse = loyalty_js_1.LoyaltyMemberLookup.extend({
    pointsBalance: zod_1.z.literal(0),
    balances: zod_1.z.array(exports.LinkLoyaltyBalance).max(exports.LINK_LOYALTY_MAX_BALANCES).optional(),
    cardAccountNumber: zod_1.z.string().min(1).max(64).optional(),
    memberRef: exports.LinkLoyaltyMemberRef.optional(),
});
/**
 * `200` of `GET /loyalty/members/{memberRef}/receipt-preference`:
 * - `required` — the customer's CRM profile asks for paper;
 * - `optional` — the customer takes an electronic receipt; paper may be
 *   skipped (only when the till's own switch allows it);
 * - `unknown` — the CRM did not answer, has no contact for the card, or the
 *   ref is unknown / expired. A till treats it as `required`.
 *
 * Only `optional` ever skips paper; a till that has no answer yet prints.
 */
exports.LinkReceiptPreference = zod_1.z.object({
    paperReceipt: zod_1.z.enum(['required', 'optional', 'unknown']),
});
/**
 * Error `code`s on the loyalty routes, each with the one HTTP status it is
 * sent with, and how a till reads it:
 *
 * | code                        | status | till reads it as                       |
 * |-----------------------------|--------|----------------------------------------|
 * | `not_found`                 | 404    | no such member (the CRM said so)       |
 * | `bad_request`               | 400    | malformed identifier                   |
 * | `unavailable`               | 503    | loyalty unavailable; sale goes on (`Retry-After`) |
 * | `upstream_error`            | 502    | server fault; sale goes on             |
 * | `rate_limited`              | 429    | server fault; sale goes on (`Retry-After`) |
 * | `store_pass_required`       | 401    | renew the pass, retry once             |
 * | `store_pass_refused`        | 401    | renew the pass, retry once             |
 * | `store_pass_scope_missing`  | 401    | renew the pass, retry once             |
 * | `refused`                   | 422    | VL-384: the CRM refused this receipt (`errorKey`) |
 *
 * `store_pass_scope_missing` is a 401, not a 403, on purpose: a till holding a
 * pass issued before its store's loyalty moved to the Link gets the scoped
 * pass on its next `sync.store-link` pull, so the ordinary renew-and-retry
 * heals it. A till that is still refused after one renewal is not granted
 * loyalty and stops asking.
 *
 * A pass the Link cannot judge (no configuration, no current revoked-till
 * list) is 503 `unavailable`, as on `/sync/*`. The receipt-preference route
 * answers an unknown, expired or other till's `memberRef` with 404
 * `not_found`; the till then prints.
 */
exports.LINK_LOYALTY_ERROR_STATUS = {
    not_found: 404,
    bad_request: 400,
    unavailable: 503,
    upstream_error: 502,
    rate_limited: 429,
    store_pass_required: 401,
    store_pass_refused: 401,
    store_pass_scope_missing: 401,
    /** VL-384: the CRM refused the receipt itself; the body carries `errorKey`. */
    refused: 422,
};
exports.LinkLoyaltyErrorCode = zod_1.z.enum(Object.keys(exports.LINK_LOYALTY_ERROR_STATUS));
/**
 * The error body, in the shape `/sync/*` refusals already have. `message` is
 * a fixed text per code — never the CRM's message, which can quote the
 * identifier. A reader tolerates a `code` it does not know and goes by the
 * status.
 */
exports.LinkLoyaltyError = zod_1.z.object({
    statusCode: zod_1.z.number().int(),
    message: zod_1.z.string().max(200),
    code: zod_1.z.string().max(64),
    /** VL-384: with `refused`, the CRM's key for why (a code, never its text). */
    errorKey: zod_1.z
        .string()
        .max(64)
        .regex(/^[A-Za-z][A-Za-z0-9_]*$/)
        .optional(),
});
// ------------------------------------------------------------------
// RL-587 — a carded receipt to the CRM, with the bonus it earned
// ------------------------------------------------------------------
/**
 * `POST` — hand the Link one finalised receipt that carries a CRM loyalty
 * card and ask for the CRM's answer within the source's timeout.
 *
 * The Link keeps every carded receipt a till uploads (`/sync/transactions`,
 * where `loyaltyCardVerification` is present) in a durable outbox and sends
 * it to the CRM — "set online", then commit — in the background, in each
 * register's order, exactly once per receipt. This route is the same
 * hand-over for ONE receipt, plus a wait: the till may call it when it wants
 * the earned bonus for a "points earned" line, and prints that line only on
 * `committed`. Whatever the answer, the receipt is in the outbox and will be
 * sent; the till never retries the CRM itself.
 *
 * The record is the one the till uploads, idempotent on the same key
 * (`<terminalId>:<clientUuid>`), so calling this and uploading the receipt
 * as usual never sends it twice.
 */
exports.LINK_LOYALTY_RECEIPT_PATH = '/loyalty/receipts';
exports.LinkLoyaltyReceiptRequest = zod_1.z.object({
    record: sync_js_1.SyncTransactionRecord,
});
/**
 * Where the receipt stands when the Link answers:
 * - `committed` — the CRM accepted and committed it; `bonusesAccrued` when
 *   the CRM said how much;
 * - `queued` — kept and to be sent: the CRM did not answer in time, is
 *   unreachable, an earlier receipt of the same register goes first, or the
 *   store's CRM source does not have receipt submission switched on yet
 *   (`reason: receipts_not_enabled`; sent once it is);
 * - `held` — kept, not sent: the till has no register number in Back
 *   Office (sent once it has), a return whose sale this Link never gave the
 *   CRM, a write whose outcome is unknown, or an uploaded void whose
 *   finalized-purchase counterpart is not documented (left for a person);
 * - `rejected` — the CRM refused this receipt (its card, a product);
 * - `not_applicable` — no CRM card on it (no `loyaltyCardVerification`), or
 *   not a sale, return or void; nothing is kept for the CRM.
 */
exports.LinkLoyaltyReceiptState = zod_1.z.enum([
    'committed',
    'queued',
    'held',
    'rejected',
    'not_applicable',
]);
/** `200` of `POST /loyalty/receipts`. */
exports.LinkLoyaltyReceiptResponse = zod_1.z.object({
    clientUuid: zod_1.z.string().min(1).max(64),
    state: exports.LinkLoyaltyReceiptState,
    /** A stable code saying why, for `held`, `rejected` and `queued`; never CRM text. */
    reason: zod_1.z
        .string()
        .max(64)
        .regex(/^[A-Za-z][A-Za-z0-9_.-]*$/)
        .optional(),
    /** Only with `committed`, and only when the CRM reported it. Display only. */
    bonusesAccrued: exports.LinkLoyaltyBalance.omit({ kind: true }).optional(),
});
// ------------------------------------------------------------------
// An open receipt, before payment (VL-384)
// ------------------------------------------------------------------
/*
 * The shapes a till's open receipt is sent to its Link in, for the CRM's
 * "set online" (VL-384, below). Extracted from VL-26's voucher contract, which
 * is not part of this cut, with the same zod shapes: `LinkVoucherBasketId` →
 * [LinkOpenReceiptBasketId], `LinkVoucherProduct` → [LinkOpenReceiptProduct].
 */
/** The till's basket id (a ULID today). */
exports.LinkOpenReceiptBasketId = zod_1.z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);
const SafeMinor = zod_1.z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
/** One live receipt line, as the CRM is to price it. Amounts in minor units. */
exports.LinkOpenReceiptProduct = zod_1.z
    .object({
    /** 1-based over the receipt's live lines; the CRM echoes it as `CashdeskPosition`. */
    position: zod_1.z.number().int().min(1).max(9999),
    /** The product code (the line's SKU). */
    code: zod_1.z.string().trim().min(1).max(64),
    /** Units, or kilograms for a weighed line. */
    quantity: zod_1.z.number().positive().max(1_000_000),
    priceMinor: SafeMinor,
    /** What the line costs as the till sends it (the till's own discounts taken off). */
    amountMinor: SafeMinor,
})
    .strict();
// ------------------------------------------------------------------
// VL-384 — online orders: the bonuses the customer redeemed on the website
// ------------------------------------------------------------------
/**
 *   POST /loyalty/online-orders/set-online   body LinkOsrSetOnlineRequest
 *
 * Same listener, guard and `loyalty` scope as the rest, with its own per-till
 * limit. The till posts the open receipt of an online order (OSR) whose
 * customer already redeemed bonuses on the website, every time its contents
 * change, and once more before payment. The Link sends the CRM's "set online"
 * with the order's own purchase key and `BonusesToWriteOff`, both UNCHANGED,
 * plus the store's receipt parameters (trade point, cash desk, time), and
 * answers what the CRM actually redeemed (`BonusesPaidAmount`).
 *
 * - **The purchase key is the order's**, not one the Link derives: the
 *   website opened the purchase under `paymentData.preprocessingId` and the
 *   receipt is committed under it (the receipt outbox reads it from the
 *   uploaded record's `extensions['rl.osr']`, `SyncOsrOrder`).
 * - **Nothing is committed here.** The commit is the receipt outbox's, after
 *   the sale was paid, fiscalised and uploaded.
 * - **Never more than asked.** An answer redeeming more than
 *   `bonusesToWriteOffMinor` is not passed on (502 `upstream_error`).
 * - **Timeout.** One CRM attempt waits at most
 *   [LINK_OSR_SET_ONLINE_ATTEMPT_TIMEOUT_MS] (or the source's own timeout when
 *   shorter); a timeout or no connection is tried once more
 *   ([LINK_OSR_SET_ONLINE_ATTEMPTS] in all — the store's line till does the
 *   same). A repeated "set online" under the same key only replaces the
 *   temporary purchase. Both attempts end before the till's own wait
 *   ([LINK_LOYALTY_TILL_TIMEOUT_MS]).
 *
 * Answers:
 * - `200` [LinkOsrSetOnlineResponse];
 * - `422 refused` with `errorKey` — the CRM refused this receipt (a blocked or
 *   unknown card, a product it does not know, a card not bound to a contact for
 *   a write-off, a body it does not accept). Asking again will not change it;
 * - every other status (`503 unavailable`, `502 upstream_error`, `429`,
 *   `400 bad_request`, `401`) — no usable answer: the till treats loyalty as
 *   down for this receipt (VL-386).
 */
exports.LINK_LOYALTY_OSR_SET_ONLINE_PATH = '/loyalty/online-orders/set-online';
/** VL-384: one CRM attempt of the online-order "set online" waits at most this long. */
exports.LINK_OSR_SET_ONLINE_ATTEMPT_TIMEOUT_MS = 10_000;
/** VL-384: attempts at the online-order "set online" when the CRM times out or cannot be reached. */
exports.LINK_OSR_SET_ONLINE_ATTEMPTS = 2;
/** VL-387: the sales channel of an online order's receipt. */
exports.OSR_CHANNEL_CODE = 1;
exports.LinkOsrSetOnlineRequest = zod_1.z
    .object({
    basketId: exports.LinkOpenReceiptBasketId,
    /** The OSR order number. Logged by neither end. */
    orderNumber: zod_1.z.string().trim().min(1).max(64),
    channelCode: zod_1.z.literal(exports.OSR_CHANNEL_CODE),
    /** The order's `paymentData.preprocessingId`, unchanged: the CRM purchase key. */
    preprocessingId: zod_1.z.string().uuid(),
    /** `BonusesToWriteOff` = the order's `paymentData.writtenOffBonuses`, minor units, unchanged. */
    bonusesToWriteOffMinor: SafeMinor,
    /** The loyalty card on the receipt. */
    cardNumber: loyalty_js_1.LoyaltyMemberNumber,
    /** The receipt amount before the bonuses: the sum of the products' `amountMinor`. */
    amountMinor: SafeMinor,
    /** The live lines, each at what it costs before the bonuses. */
    products: zod_1.z.array(exports.LinkOpenReceiptProduct).min(1).max(500),
})
    .strict()
    .superRefine((request, ctx) => {
    const seen = new Set();
    let sum = 0;
    for (const product of request.products) {
        if (seen.has(product.position)) {
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'duplicate position' });
            return;
        }
        seen.add(product.position);
        sum += product.amountMinor;
    }
    if (sum !== request.amountMinor) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['amountMinor'],
            message: 'not the sum of the products',
        });
    }
});
/** `200` of the online-order "set online". */
exports.LinkOsrSetOnlineResponse = zod_1.z
    .object({
    basketId: exports.LinkOpenReceiptBasketId,
    preprocessingId: zod_1.z.string().uuid(),
    /** `BonusesPaidAmount`: what the CRM redeemed. Never above `bonusesToWriteOffMinor`. */
    bonusesPaidMinor: SafeMinor,
    /** `TotalPaidAmount` («Усього оплачено»), when the CRM answered one. */
    totalPaidMinor: SafeMinor.optional(),
    /** `FinalCashPaidAmount`, when the CRM answered one. */
    finalCashPaidMinor: SafeMinor.optional(),
})
    .strict();
// ------------------------------------------------------------------
// VL-26 successor — the voucher check (DORMANT)
// ------------------------------------------------------------------
/**
 * `POST /loyalty/vouchers/check` — a store CRM voucher's status, and what a
 * ledgered purchase must carry to apply it (`GET /api/Voucher/by/{number}`).
 * Served only while the Link's `crmPurchaseVouchers` switch is on (default
 * off); otherwise the path is not served at all. Read-only: no purchase.
 *
 * - `status`: `active` for Creatio `StatusCode` 0/2, `redeemed` for 3/4
 *   (Varus Q-V1: 4 is treated as used), else read from the status name. It
 *   says nothing about applicability: 2 also covers reserved and expired;
 *   only the purchase answer's `voucher.used` decides.
 * - `voucherCode`: Creatio's `VoucherCode`, verbatim (zeros kept), what the
 *   purchase's `vouchers[].code` must be. Left out when Creatio did not
 *   answer it as a string of that shape.
 * - `applyPromotionId`: Creatio's `ApplyPromotionId`. Left out when absent.
 * - A voucher the CRM does not have is 404 `not_found`.
 */
exports.LINK_LOYALTY_VOUCHER_CHECK_PATH = '/loyalty/vouchers/check';
/** A printed voucher number as scanned: it goes into the CRM's path, so a safe shape only. */
exports.LinkVoucherCode = zod_1.z.string().regex(/^[A-Za-z0-9_-]{3,64}$/);
exports.LinkVoucherCheckRequest = zod_1.z.object({ code: exports.LinkVoucherCode }).strict();
exports.LinkVoucherStatus = zod_1.z.enum(['active', 'redeemed', 'expired', 'inactive']);
exports.LinkVoucherCheckResponse = zod_1.z
    .object({
    code: exports.LinkVoucherCode,
    status: exports.LinkVoucherStatus,
    voucherCode: link_crm_purchase_js_1.LinkPurchaseVoucherCode.optional(),
    applyPromotionId: zod_1.z.string().uuid().optional(),
})
    .strict();
//# sourceMappingURL=link-loyalty.js.map